import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Stamp } from "lucide-react";
import { Badge } from "../../components/Badge";
import { api } from "../../api/client";
import { formatMoney } from "../../lib/money";
import { LEAVE_TYPE_SHORT_LABEL } from "../../api/types";
import type { InvoiceRequest, LeaveRequest } from "../../api/types";

// 待我核准(業主 2026-09-17:「+待我核准 — 老闆專屬」)。
//
// ==========================================================================
// 為什麼不靠通知就好
// ==========================================================================
// 通知**看過就會被清掉**(這一輪還加了「刪除已讀」),而一件還沒處理的事
// 不該只活在一則已讀通知裡。這一塊只要還有人在等,它就一直在 ——
// 它讀的是「現在是不是還 PENDING」,不是「有沒有人通知過我」。
//
// ==========================================================================
// 兩種事,一份清單
// ==========================================================================
// 請假申請與開票申請。差別只在點下去要去哪一頁,所以合成一份、
// **每一列帶一枚寫著字的徽章**(「請假」/「開票」)—— 只靠顏色分的話,
// 色盲與灰階列印完全分不出來,而這兩件事的處理方式完全不同。
//
// ==========================================================================
// 讀不到就說讀不到
// ==========================================================================
// 畫成空的等於在說「沒事了」,而老闆會據此收工。這與通知面板同一條規矩。

/** 一列要畫的東西。兩種來源攤平成同一個形狀。 */
interface Row {
  key: string;
  kind: "leave" | "billing";
  who: string;
  what: string;
  when: string;
  to: string;
}

function leaveRow(one: LeaveRequest): Row {
  return {
    key: `leave-${one.id}`,
    kind: "leave",
    who: one.user_name,
    what: `${LEAVE_TYPE_SHORT_LABEL[one.leave_type]} ${one.days} 天`,
    when: one.start_date === one.end_date ? one.start_date : `${one.start_date} – ${one.end_date}`,
    to: "/leave",
  };
}

function billingRow(one: InvoiceRequest): Row {
  return {
    key: `billing-${one.id}`,
    kind: "billing",
    who: one.requester_name,
    // 單號與期別都要:老闆看到的是「哪一張單的第幾期」,只有金額看不出是誰。
    what: `${one.quote_no} ${one.payment_term_name} ${formatMoney(one.amount)}`,
    when: one.wanted_date ?? "",
    to: "/invoices",
  };
}

export function ApprovalQueue() {
  const [rows, setRows] = useState<Row[]>([]);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    // 兩支一起發。串著發的話,清單會先跳出一半再補上另一半。
    Promise.all([
      api.get<LeaveRequest[]>("/leave", { status: "PENDING" }),
      api.get<InvoiceRequest[]>("/invoice-requests", { status: "PENDING" }),
    ])
      .then(([leave, billing]) => {
        if (!alive) return;
        setRows([
          ...(Array.isArray(leave) ? leave : []).map(leaveRow),
          ...(Array.isArray(billing) ? billing : []).map(billingRow),
        ]);
        setFailed(false);
      })
      .catch(() => {
        if (!alive) return;
        setRows([]);
        setFailed(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section
      aria-label="待我核准"
      className="flex min-w-0 flex-col gap-3 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 flex items-center gap-2 text-base font-semibold text-[var(--text)]">
          <Stamp aria-hidden="true" size={16} />
          待我核准
        </h2>
        {rows.length > 0 ? <Badge tone="warn">{`${rows.length} 件`}</Badge> : null}
      </div>

      {failed ? (
        // 讀不到就寫讀不到。畫成空的是在宣稱一件不知道真假的事,
        // 而老闆會據此收工。
        <p role="status" className="m-0 text-sm text-[var(--text-2)]">
          待核准的事暫時讀不到,稍後再開一次。
        </p>
      ) : loading ? (
        <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p>
      ) : rows.length === 0 ? (
        <p className="m-0 text-sm text-[var(--text-3)]">目前沒有要你核准的事。</p>
      ) : (
        <ul aria-label="待核准清單" className="m-0 flex list-none flex-col gap-1 p-0">
          {rows.map((row) => (
            <li
              key={row.key}
              className="flex flex-wrap items-center gap-2 border-t border-[var(--border)] py-1.5 text-sm first:border-t-0"
            >
              {/* 顏色**加上**文字 —— 兩件事的處理方式完全不同。 */}
              <Badge tone={row.kind === "leave" ? "accent" : "warn"}>
                {row.kind === "leave" ? "請假" : "開票"}
              </Badge>
              <span className="shrink-0 text-[var(--text)]">{row.who}</span>
              <Link
                to={row.to}
                className="min-w-0 flex-1 truncate text-[var(--accent-hover)] underline-offset-2 hover:underline"
              >
                {row.what}
              </Link>
              {row.when ? (
                <span className="tabular shrink-0 text-xs text-[var(--text-3)]">{row.when}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
